import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { isColorDark } from './public/utils';
import ProductCard from './public/components/ProductCard';
import SiteHeader from './public/components/SiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicCategory = () => {
  const { slug, categoryId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/shops/${slug}`);
      setData(res.data);
    } catch (error) {
      toast.error('Catalogue non trouve');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatPrice = (cents) => {
    if (!cents) return null;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="w-12 h-12 rounded-full border-4 border-zinc-800 border-t-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
          <h1 className="text-2xl font-bold text-white mb-3">Categorie introuvable</h1>
          <Button onClick={() => navigate(`/s/${slug}`)} className="rounded-full">Retour</Button>
        </div>
      </div>
    );
  }

  const { shop, categories, products } = data;
  const category = categories.find(c => c.id === categoryId);
  const catProducts = products.filter(p => p.categoryId === categoryId);
  const accentColor = shop.accentColor || '#F59E0B';
  const pageBg = shop.backgroundColor || '#09090B';
  const cardBg = shop.cardColor || '#18181B';
  const textColor = shop.textColor || '#FAFAFA';
  const isDark = isColorDark(pageBg);
  const headerColor = shop.headerColor || accentColor;

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg, color: textColor }} data-testid="public-category">
      {/* Simple header */}
      <header style={{ backgroundColor: headerColor }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/s/${slug}`)} className="p-1 rounded-lg" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h1 className="font-bold text-lg" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>
            {category?.name || 'Categorie'}
          </h1>
        </div>
      </header>

      {/* Category nav */}
      <div style={{ backgroundColor: cardBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => navigate(`/s/${slug}/c/${cat.id}`)}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                style={cat.id === categoryId ? { backgroundColor: accentColor, color: 'black' } : { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
                data-testid={`cat-nav-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-8">
        {catProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="opacity-50">Aucun produit dans cette categorie</p>
          </div>
        ) : (
          <div className="space-y-3">
            {catProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                slug={slug}
                accentColor={accentColor}
                cardBg={cardBg}
                isDark={isDark}
                formatPrice={formatPrice}
                shopName={shop.name}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicCategory;
